import { AlertErrorOutline, CheckCircleOutline } from '../../svg'
import { cn } from '../../utils'
import * as Toast from '@radix-ui/react-toast'
import React, { useEffect, useState } from 'react'
import {
    getMessageClasses,
    getMessageColor,
    getRootColor,
    getSnackBarRootClasses,
} from './SnackBar.styles'
import { SnackBarProps } from './SnackBar.types'

export const SnackBarProvider = ({
    children,
}: {
    children: React.ReactNode
}) => (
    <Toast.Provider swipeDirection="right">
        {children}
        <Toast.Viewport
            className="fixed bottom-4 left-1/2 flex w-auto max-w-[90vw] -translate-x-1/2 flex-col gap-2 outline-none sm:bottom-auto sm:top-5"
            style={{ zIndex: 'var(--z-toast, 70)' }}
        />
    </Toast.Provider>
)

/** Minimum time (ms) an error toast stays up before closing itself. */
const ERROR_MIN_DURATION = 6000

export const SnackBar = ({
    id,
    open,
    variant = 'success',
    notificationMessage,
    delay = 3000,
    snackBarIcon,
    rootClasses,
    onDismiss,
}: SnackBarProps) => {
    const isError = variant === 'error'
    // delay 0 means "stay until closed" (e.g. login's verification warning
    // next to its resend button). Radix reads 0 as "use the provider default"
    // (5 s), so it has to be passed as Infinity. Errors close on their own
    // too — they used to stay forever — but get longer on screen, since they
    // usually carry something to read and act on.
    const duration =
        delay === 0
            ? Infinity
            : isError
              ? Math.max(delay, ERROR_MIN_DURATION)
              : delay

    // Own the open state so the close button (and swipe / timeout) actually
    // closes the toast. It used to be fully controlled by `open`, and since
    // callers don't pass `onDismiss`, nothing ever set it to false — the X
    // did nothing, and error toasts (no timeout) stayed until the parent
    // cleared them. A new `open` or message from the parent re-opens it.
    const [isOpen, setIsOpen] = useState(open)

    useEffect(() => {
        setIsOpen(open)
    }, [open, notificationMessage])

    const getIcon = () => {
        if (snackBarIcon) {
            return snackBarIcon
        }
        if (variant === 'success') {
            return <CheckCircleOutline />
        }
        return <AlertErrorOutline />
    }

    const handleOpenChange = (nextOpen: boolean) => {
        setIsOpen(nextOpen)
        if (!nextOpen) onDismiss?.()
    }

    return (
        <Toast.Root
            id={`${id}-snack-bar`}
            className={cn(
                getSnackBarRootClasses(),
                rootClasses,
                getRootColor(variant),
                'data-[state=closed]:animate-slide-out data-[state=open]:animate-slide-in'
            )}
            open={isOpen}
            duration={duration}
            onOpenChange={handleOpenChange}
        >
            <Toast.Title
                className={cn(getMessageClasses(), getMessageColor(variant))}
            >
                <span className="mr-[12px] shrink-0 text-white">
                    {getIcon()}
                </span>
                <span className="break-words text-white">
                    {notificationMessage}
                </span>
            </Toast.Title>
            <Toast.Close className="-m-2 ml-3 shrink-0 p-2 text-white opacity-70 hover:opacity-100 focus:outline-none">
                ✕
            </Toast.Close>
        </Toast.Root>
    )
}
